import type { SketchEntityReferencePreviewServiceDependencies } from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferencePreviewService';
import type { SketchEntityReferencePreviewBatchResult } from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferencePreviewCoordinator';
import type { CreateGeometryReferencesInput } from '../../model/SketchEntityReferenceTypes';
import { createExecutionDiagnostic, type ExecutionDiagnostic } from '../../model/SketchEntityReferenceDiagnostics';
import { BuildGeometryReferencePreviewBatchService } from './BuildGeometryReferencePreviewBatchService';
import { SketchEntityReferenceProjectionService } from '../../source/projection/SketchEntityReferenceProjectionService';
import type { SketchEntityReferenceProjectionPort } from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferenceProjectionService';

export type {
	SketchEntityReferencePreviewServiceDependencies,
	SketchEntityReferencePreviewServiceRuntime,
} from '../../../../../types/reference/sketch-entity-reference/SketchEntityReferencePreviewService';

/**
 * 草图实体引用 Preview 应用服务。
 *
 * 说明：
 * 1. 本服务把创建输入求值为尚未激活的 Preview 批次；
 * 2. 内部串起目标草图解析、来源投影归一化和 Preview 对象构建；
 * 3. 本服务不持有命令级状态，不管理 revision、队列或生命周期。
 */
export class SketchEntityReferencePreviewService {
	/** Preview 对象批次构建能力。 */
	private readonly buildPreviewBatchService: BuildGeometryReferencePreviewBatchService;
	/** 来源描述到投影结果的公共求值能力。 */
	private readonly projectionService: SketchEntityReferenceProjectionPort;

	/**
	 * 创建 Preview 应用服务。
	 *
	 * @param dependencies 文档运行时和投影求值依赖。
	 */
	public constructor(private readonly dependencies: SketchEntityReferencePreviewServiceDependencies) {
		this.buildPreviewBatchService = dependencies.buildPreviewBatchService ?? new BuildGeometryReferencePreviewBatchService();
		this.projectionService =
			dependencies.projectionService ??
			new SketchEntityReferenceProjectionService({
				runtime: dependencies.runtime,
				sourceResolver: dependencies.sourceResolver,
				lineProjectionPreprocessor: dependencies.lineProjectionPreprocessor,
				circleProjectionPreprocessor: dependencies.circleProjectionPreprocessor,
				arcProjectionPreprocessor: dependencies.arcProjectionPreprocessor,
				ellipseProjectionPreprocessor: dependencies.ellipseProjectionPreprocessor,
				ellipseArcProjectionPreprocessor: dependencies.ellipseArcProjectionPreprocessor,
				parabolaProjectionPreprocessor: dependencies.parabolaProjectionPreprocessor,
				splineProjectionPreprocessor: dependencies.splineProjectionPreprocessor,
				styleSplineProjectionPreprocessor: dependencies.styleSplineProjectionPreprocessor,
			});
	}

	/**
	 * 基于创建输入构建尚未激活的 Preview 批次。
	 *
	 * @param input 转换实体引用创建输入。
	 * @returns Preview 批次及求值诊断。
	 */
	public async buildPreviewBatch(input: CreateGeometryReferencesInput): Promise<SketchEntityReferencePreviewBatchResult> {
		const targetSketch = this.dependencies.runtime.getSketchGroup(input.targetSketchId);
		if (!targetSketch) {
			return {
				createdEntities: [],
				previewObjects: [],
				diagnostics: [this.createMissingTargetSketchDiagnostic(input.targetSketchId)],
			};
		}

		const normalizedResult = this.projectionService.project({
			targetSketch,
			sourceDescriptors: input.sources,
			runtimeTopologySourcesBySourceKey: input.runtimeTopologySourcesBySourceKey,
		});
		const previewBatch = this.buildPreviewBatchService.build({
			targetSketch,
			projectionResults: normalizedResult.projectionResults,
		});

		return {
			createdEntities: previewBatch.createdEntities,
			previewObjects: previewBatch.previewObjects,
			diagnostics: [...normalizedResult.diagnostics, ...previewBatch.diagnostics],
			activatePreviewEntities: previewBatch.activatePreviewEntities,
		};
	}

	/**
	 * 构造目标草图缺失诊断。
	 *
	 * @param targetSketchId 目标草图 ID。
	 * @returns 规范化执行诊断。
	 */
	private createMissingTargetSketchDiagnostic(targetSketchId: string): ExecutionDiagnostic {
		return createExecutionDiagnostic({
			kind: 'input-invalid',
			level: 'error',
			message: `未找到 preview 目标草图: ${targetSketchId}`,
		});
	}
}
